import {
  Document,
  Font,
  Page,
  StyleSheet,
  Text,
  View,
  Image,
} from "@react-pdf/renderer";
import { convertToLocalTime } from "../../../../utils/dateTimeConversionUtils";
import NotoSansRegular from "../../../../Assets/Fonts/NotoSans-Regular.ttf";
import NotoSansBold from "../../../../Assets/Fonts/NotoSans-Bold.ttf";

// Font Registration
Font.register({
  family: "NotoSans",
  fonts: [
    { src: NotoSansRegular, fontWeight: "normal" },
    { src: NotoSansBold, fontWeight: "bold" },
  ],
});

const styles = StyleSheet.create({
  page: {
    padding: 24,
    fontFamily: "NotoSans",
    fontSize: 9,
    lineHeight: 1.6,
    color: "#374151",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottom: "1 solid #e5e7eb",
    paddingBottom: 12,
    marginBottom: 16,
  },
  section: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 16,
    flexWrap: "wrap",
  },
  block: {
    width: "23%",
    marginBottom: 10,
  },
  bold: {
    fontFamily: "NotoSans",
    fontWeight: 600,
    textTransform: "uppercase",
    marginBottom: 4,
  },
  muted: {
    color: "#6b7280",
  },
  table: {
    border: "1 solid #e5e7eb",
    borderRadius: 6,
    overflow: "hidden",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f3f4f6",
    borderBottom: "1 solid #e5e7eb",
  },
  tableRow: {
    flexDirection: "row",
    borderBottom: "1 solid #f3f4f6",
  },
  col: {
    width: "25%",
    padding: 6,
  },
  colTextCenter: {
    textAlign: "center",
  },
  colTextRight: {
    textAlign: "right",
  },
  totalSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingTop: 12,
    borderTop: "1 solid #d1d5db",
    marginTop: 16,
    flexWrap: "wrap",
  },
});

const DownloadReturn = ({ data }) => {
  const { customer, store, items } = data;
  const address = store?.address || {};
  const billing = customer?.billing_address || {};

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View>
            <Text style={styles.bold}>Return Invoice</Text>
            <Text style={styles.muted}>
              Status: <Text style={{ color: "#eab308" }}>{data?.status || " "}</Text>
            </Text>
          </View>
          {store?.logo && <Image src={store.logo} style={{ width: 40, height: 40 }} />}
        </View>

        {/* Info Section */}
        <View style={styles.section}>
          {/* Return From */}
          <View style={styles.block}>
            <Text style={styles.bold}>Return From</Text>
            <Text style={styles.muted}>
              {store?.name}
              <Text>
                {address?.address1}, {address?.address2}, {address?.address3}
              </Text>
              <Text>
                {address?.city}, {address?.state} - {address?.pincode}
              </Text>
              {store?.gst_no && <Text>GST: {store?.gst_no}</Text>}
              {store?.fssai_no && <Text>FSSAI: {store?.fssai_no}</Text>}
            </Text>
          </View>

          {/* Date */}
          <View style={styles.block}>
            <Text style={styles.bold}>Date</Text>
            <Text style={styles.muted}>
              {convertToLocalTime(data.invoice_date, "DD/MM/YYYY")}
            </Text>
          </View>

          {/* Return No */}
          <View style={styles.block}>
            <Text style={styles.bold}>Return Number</Text>
            <Text style={styles.muted}># {data.id || "-"}</Text>
          </View>

          {/* Return To */}
          <View style={styles.block}>
            <Text style={styles.bold}>Return To</Text>
            <Text style={styles.muted}>
              {customer?.name}
              <Text>
                {customer?.phone}, {customer?.email}
              </Text>
              <Text>
                {billing?.address1}, {billing?.address2}, {billing?.address3}
              </Text>
              <Text>
                {billing?.state}, {billing?.city} - {billing?.pincode}
              </Text>
            </Text>
          </View>
        </View>

        {/* Item Table */}
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <View style={styles.col}>
              <Text style={styles.bold}>Product</Text>
            </View>
            <View style={styles.col}>
              <Text style={[styles.bold, styles.colTextCenter]}>Quantity</Text>
            </View>
            <View style={styles.col}>
              <Text style={styles.bold}>Reason</Text>
            </View>
            <View style={styles.col}>
              <Text style={[styles.bold, styles.colTextRight]}>Amount (₹)</Text>
            </View>
          </View>

          {items?.map((item, idx) => (
            <View key={idx} style={styles.tableRow}>
              <View style={styles.col}>
                <Text>{item.product_name}</Text>
              </View>
              <View style={styles.col}>
                <Text style={styles.colTextCenter}>{item.quantity}</Text>
              </View>
              <View style={styles.col}>
                <Text>{item.return_reason?.reason || "-"}</Text>
              </View>
              <View style={styles.col}>
                <Text style={styles.colTextRight}>{item.total_price}</Text>
              </View>
            </View>
          ))}
        </View>

        {/* Totals Section */}
        <View style={styles.totalSection}>
          <View style={styles.block}>
            <Text style={styles.bold}>Payment Method</Text>
            <Text style={{ color: "#0e9f6e" }}>{data.payment_method || "-"}</Text>
          </View>
          <View style={styles.block}>
            <Text style={styles.bold}>Sub Total (₹)</Text>
            <Text>{data.sub_total || 0}</Text>
          </View>
          <View style={styles.block}>
            <Text style={styles.bold}>Total (₹)</Text>
            <Text style={{ color: "#ef4444", fontWeight: 600 }}>
              {data.total_amount || 0}
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default DownloadReturn;